/**
 * Google Calendar API helpers (placeholder for task ↔ event sync).
 *
 * Important: Calendar API calls require a **Google OAuth2 access token**
 * from `GoogleAuthProvider.credentialFromResult()`, not a Firebase ID token.
 */

import { GoogleAuthProvider } from 'firebase/auth';

const CALENDAR_API_BASE = 'https://www.googleapis.com/calendar/v3';

/**
 * Resolve a Google OAuth2 access token from a Firebase Google sign-in result.
 * @param {import('firebase/auth').UserCredential | { accessToken?: string }} credentialOrResult
 * @returns {string|null}
 */
export function resolveGoogleAccessToken(credentialOrResult) {
  if (!credentialOrResult) return null;
  if (typeof credentialOrResult.accessToken === 'string') {
    return credentialOrResult.accessToken;
  }
  const cred = GoogleAuthProvider.credentialFromResult(credentialOrResult);
  return cred?.accessToken ?? null;
}

/**
 * List upcoming events from the user's primary calendar.
 *
 * @param {string} accessToken Google OAuth2 access token (see resolveGoogleAccessToken)
 * @param {{ calendarId?: string, maxResults?: number, timeMin?: string }} [options]
 * @returns {Promise<{ items: object[], raw: object }>}
 */
export async function listUpcomingCalendarEvents(accessToken, options = {}) {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new Error(
      'Google OAuth access token is required. Sign in with Google and request the calendar.events scope.',
    );
  }

  const calendarId = encodeURIComponent(options.calendarId || 'primary');
  const params = new URLSearchParams({
    maxResults: String(options.maxResults ?? 10),
    singleEvents: 'true',
    orderBy: 'startTime',
    timeMin: options.timeMin ?? new Date().toISOString(),
  });

  const url = `${CALENDAR_API_BASE}/calendars/${calendarId}/events?${params.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      `Google Calendar API error (${response.status}): ${body || response.statusText}`,
    );
  }

  const raw = await response.json();
  return {
    items: Array.isArray(raw.items) ? raw.items : [],
    raw,
  };
}

/**
 * Placeholder: create a calendar event from an agile/task payload.
 * @param {string} accessToken
 * @param {{ title: string, description?: string, start: string, end: string, calendarId?: string }} event
 */
export async function createCalendarEventFromTask(accessToken, event) {
  if (!accessToken) {
    throw new Error('Google OAuth access token is required.');
  }

  const calendarId = encodeURIComponent(event.calendarId || 'primary');
  const url = `${CALENDAR_API_BASE}/calendars/${calendarId}/events`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      summary: event.title,
      description: event.description || '',
      start: { dateTime: event.start },
      end: { dateTime: event.end },
    }),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Failed to create calendar event (${response.status}): ${body}`);
  }

  return response.json();
}

/**
 * Persist Google Calendar refresh token server-side for background sync.
 * @param {{ refreshToken?: string|null, accessToken?: string|null }} tokens
 */
export async function saveCalendarRefreshTokenToServer(tokens) {
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('../firebase');
  const fn = httpsCallable(functions, 'saveCalendarRefreshToken');
  const result = await fn({
    refreshToken: tokens.refreshToken || null,
    accessToken: tokens.accessToken || null,
  });
  return result.data;
}
